import { Link } from "react-router-dom";

import type {
  AssetBaseResponse,
  AssetType,
  LifecycleState,
  RiskClassification,
} from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2 } from "lucide-react";
import { LIFECYCLE_TONE, RISK_TONE } from "@/lib/asset-enums";
import { formatRelativeTimeID } from "@/lib/format";
import { ROUTES } from "@/lib/constants";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";

// Enum values map to translation keys so badge labels follow the
// active locale.
const ASSET_TYPE_KEY: Record<AssetType, TranslationKey> = {
  prompt: "assets.types.prompt",
  model: "assets.types.model",
  agent: "assets.types.agent",
  workflow: "assets.types.workflow",
  provider: "assets.types.provider",
  tool: "assets.types.tool",
  dataset: "assets.types.dataset",
};

const LIFECYCLE_KEY: Record<LifecycleState, TranslationKey> = {
  draft: "assets.status.draft",
  review: "assets.status.review",
  active: "assets.status.active",
  deprecated: "assets.status.deprecated",
  retired: "assets.status.retired",
};

const RISK_KEY: Record<RiskClassification, TranslationKey> = {
  low: "assets.risk.low",
  medium: "assets.risk.medium",
  high: "assets.risk.high",
  critical: "assets.risk.critical",
};

interface AssetsListProps {
  items: AssetBaseResponse[];
  isLoading: boolean;
  onDelete: (asset: AssetBaseResponse) => void;
}

export function AssetsList({ items, isLoading, onDelete }: AssetsListProps) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("assets.table.name")}</TableHead>
            <TableHead>{t("assets.table.type")}</TableHead>
            <TableHead>{t("assets.table.lifecycle")}</TableHead>
            <TableHead>{t("assets.table.risk")}</TableHead>
            <TableHead className="hidden tablet:table-cell">
              {t("assets.table.tags")}
            </TableHead>
            <TableHead className="hidden desktop:table-cell">
              {t("assets.table.updated")}
            </TableHead>
            <TableHead className="w-12 text-right">
              {t("assets.table.actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && items.length === 0 && (
            <>
              {[0, 1, 2].map((i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={7} className="py-4">
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            </>
          )}
          {!isLoading && items.length === 0 && (
            <TableEmpty
              colSpan={7}
              message={t("assets.table.emptyFiltered")}
            />
          )}
          {items.map((asset) => (
            <TableRow key={asset.id}>
              <TableCell>
                <Link
                  to={`${ROUTES.ASSETS}/${asset.id}`}
                  className="font-medium text-ocean-900 hover:text-ocean-600"
                >
                  {asset.name}
                </Link>
                {asset.description && (
                  <div className="mt-0.5 line-clamp-1 text-xs text-neutral-500">
                    {asset.description}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <Badge tone="ocean">
                  {t(ASSET_TYPE_KEY[asset.asset_type])}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge tone={LIFECYCLE_TONE[asset.lifecycle_state]}>
                  {t(LIFECYCLE_KEY[asset.lifecycle_state])}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge tone={RISK_TONE[asset.risk_classification]}>
                  {t(RISK_KEY[asset.risk_classification])}
                </Badge>
              </TableCell>
              <TableCell className="hidden text-xs text-neutral-600 tablet:table-cell">
                {asset.tags.length === 0 ? (
                  <span className="text-neutral-400">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {asset.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="rounded bg-ocean-50 px-1.5 py-0.5 text-[10px]"
                      >
                        {tag}
                      </span>
                    ))}
                    {asset.tags.length > 3 && (
                      <span className="text-[10px]">
                        +{asset.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell className="hidden text-neutral-600 desktop:table-cell">
                {formatRelativeTimeID(asset.updated_at)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t("assets.actions.deleteRowAriaLabel", {
                    name: asset.name,
                  })}
                  onClick={() => onDelete(asset)}
                >
                  <Trash2 size={14} className="text-danger-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
